import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaMoon, FaSun, FaDesktop, FaGlobe, FaMoneyBillWave, FaFlag } from 'react-icons/fa';
import { getFirebase } from '@services/firebase';
import { doc, setDoc } from 'firebase/firestore';

const CURRENCIES = [
  { code: 'GBP', label: '£ British Pound' },
  { code: 'EUR', label: '€ Euro' },
  { code: 'USD', label: '$ US Dollar' },
  { code: 'INR', label: '₹ Indian Rupee' },
  { code: 'AED', label: 'د.إ UAE Dirham' },
  { code: 'CAD', label: 'C$ Canadian Dollar' },
  { code: 'AUD', label: 'A$ Australian Dollar' },
  { code: 'SGD', label: 'S$ Singapore Dollar' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ta', label: 'தமிழ்' },
];

export const COUNTRIES = [
  { code: 'DE', label: '🇩🇪 Germany', isd: '+49' },
  { code: 'GB', label: '🇬🇧 United Kingdom', isd: '+44' },
  { code: 'US', label: '🇺🇸 United States', isd: '+1' },
  { code: 'IN', label: '🇮🇳 India', isd: '+91' },
  { code: 'AE', label: '🇦🇪 UAE', isd: '+971' },
  { code: 'CA', label: '🇨🇦 Canada', isd: '+1' },
  { code: 'AU', label: '🇦🇺 Australia', isd: '+61' },
  { code: 'SG', label: '🇸🇬 Singapore', isd: '+65' },
  { code: 'FR', label: '🇫🇷 France', isd: '+33' },
  { code: 'AT', label: '🇦🇹 Austria', isd: '+43' },
  { code: 'CH', label: '🇨🇭 Switzerland', isd: '+41' },
  { code: 'NL', label: '🇳🇱 Netherlands', isd: '+31' },
];

export const getAppISD = () => {
  const code = localStorage.getItem('appCountry') || 'DE';
  return COUNTRIES.find(c => c.code === code)?.isd || '+49';
};

const COUNTRY_TIMEZONE_MAP = {
  DE: 'Europe/Berlin',
  AT: 'Europe/Vienna',
  CH: 'Europe/Zurich',
  NL: 'Europe/Amsterdam',
  FR: 'Europe/Paris',
  GB: 'Europe/London',
  US: 'America/New_York',
  CA: 'America/Toronto',
  AU: 'Australia/Sydney',
  IN: 'Asia/Kolkata',
  AE: 'Asia/Dubai',
  SG: 'Asia/Singapore',
};

export const getAppTimezone = () => {
  const code = localStorage.getItem('appCountry') || 'DE';
  return COUNTRY_TIMEZONE_MAP[code] || 'Europe/Berlin';
};

export const formatChatTime = (isoString) => {
  if (!isoString) return '';
  const tz = getAppTimezone();
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const todayStr = now.toLocaleDateString('en-GB', { timeZone: tz });
  const dateStr = date.toLocaleDateString('en-GB', { timeZone: tz });
  const time = date.toLocaleTimeString([], { timeZone: tz, hour: '2-digit', minute: '2-digit' });

  if (dateStr === todayStr) return time;

  const day = date.toLocaleString('en-GB', { timeZone: tz, day: '2-digit' });
  const mon = date.toLocaleString('en-GB', { timeZone: tz, month: 'short' });

  if (date.getFullYear() === now.getFullYear()) return `${time} ${day} ${mon}`;

  const yr = String(date.getFullYear()).slice(-2);
  return `${time} ${day} ${mon} ${yr}`;
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    root.setAttribute('data-theme', theme);
  }
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) metaTheme.setAttribute('content', '#fa4848');
};

const Settings = ({ userRole }) => {
  const navigate = useNavigate();

  const [theme, setTheme] = useState(() => localStorage.getItem('appTheme') || 'system');
  const [currency, setCurrency] = useState(() => localStorage.getItem('appCurrency') || 'EUR');
  const [language, setLanguage] = useState(() => localStorage.getItem('appLanguage') || 'en');
  const [country, setCountry] = useState(() => localStorage.getItem('appCountry') || 'DE');
  const handleTheme = (val) => {
    setTheme(val);
    localStorage.setItem('appTheme', val);
    applyTheme(val);
  };

  const handleCurrency = async (val) => {
    setCurrency(val);
    localStorage.setItem('appCurrency', val);
    const { db } = getFirebase();
    await setDoc(doc(db, 'siteConfig', 'appSettings'), { currency: val }, { merge: true });
  };

  const handleLanguage = async (val) => {
    setLanguage(val);
    localStorage.setItem('appLanguage', val);
    const { db } = getFirebase();
    await setDoc(doc(db, 'siteConfig', 'appSettings'), { language: val }, { merge: true });
  };

  const handleCountry = async (val) => {
    setCountry(val);
    localStorage.setItem('appCountry', val);
    window.dispatchEvent(new CustomEvent('appCountryChanged'));
    const { db } = getFirebase();
    await setDoc(doc(db, 'siteConfig', 'appSettings'), { country: val }, { merge: true });
  };

  return (
    <>
      <style>{`
        .st-container {
          display: flex;
          flex-direction: column;
          padding: 5px;
          padding-bottom: 50px;
          background-color: var(--bg-primary, #f5f5f5);
          min-height: calc(100vh - 50px);
          width: 100%;
          box-sizing: border-box;
        }

        .st-content {
          position: relative;
          background: var(--bg-surface, #ffffff);
          border-radius: 8px;
          width: 80vw;
          max-width: 500px;
          padding: 15px;
          margin: 10px auto;
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          box-sizing: border-box;
        }

        .st-close-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: none;
          border: none;
          font-size: 16px;
          cursor: pointer;
          color: var(--text-secondary, #666);
          padding: 4px;
          min-width: unset;
          min-height: unset;
          z-index: 10;
        }

        .st-close-btn:hover {
          color: var(--text-primary, #333);
          background: none;
        }

        .st-page-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-primary, #333);
          margin: 0 0 4px 0;
          text-align: center;
        }

        .st-section-header {
          display: flex;
          align-items: center;
          gap: 7px;
          background-color: var(--bg-section, #f0f0f0);
          padding: 6px 10px;
          border-radius: 5px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary, #666);
          margin-bottom: 8px;
        }

        .st-section-header svg {
          color: #fa4848;
          flex-shrink: 0;
        }

        .theme-toggle-group {
          display: flex;
          gap: 8px;
        }

        .theme-btn {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          padding: 10px 6px;
          border-radius: 8px;
          border: 2px solid var(--border-color, #e0e0e0);
          background: var(--bg-primary, #f5f5f5);
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary, #888);
          transition: all 0.15s;
          min-width: unset;
          min-height: unset;
        }

        .theme-btn svg {
          font-size: 17px;
          color: var(--text-secondary, #aaa);
          transition: color 0.15s;
        }

        .theme-btn--active {
          border-color: #fa4848;
          background: #fff1f1;
          color: #fa4848;
        }

        .theme-btn--active svg {
          color: #fa4848;
        }

        .st-select {
          width: 100%;
          border: 1.5px solid var(--border-color, #ddd);
          border-radius: 6px;
          padding: 8px 32px 8px 10px;
          font-size: 13px;
          color: var(--text-primary, #333);
          background-color: var(--bg-primary, #f5f5f5);
          outline: none;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          box-sizing: border-box;
        }

        .st-select:focus {
          border-color: #fa4848;
        }

        @media (max-width: 450px) {
          .st-content { width: 94vw; padding: 12px; }
          .theme-btn { font-size: 10px; padding: 8px 4px; }
          .theme-btn svg { font-size: 15px; }
          .st-select { font-size: 12px; }
        }
      `}</style>

      <div className="st-container">
        <div className="st-content">
          <button className="st-close-btn" onClick={() => navigate(-1)}>
            <FaTimes />
          </button>

          <p className="st-page-title">Settings</p>

          {/* Theme */}
          <div>
            <div className="st-section-header">
              <FaDesktop /> Screen Theme
            </div>
            <div className="theme-toggle-group">
              <button
                className={`theme-btn${theme === 'light' ? ' theme-btn--active' : ''}`}
                onClick={() => handleTheme('light')}
              >
                <FaSun />
                Light
              </button>
              <button
                className={`theme-btn${theme === 'dark' ? ' theme-btn--active' : ''}`}
                onClick={() => handleTheme('dark')}
              >
                <FaMoon />
                Dark
              </button>
              <button
                className={`theme-btn${theme === 'system' ? ' theme-btn--active' : ''}`}
                onClick={() => handleTheme('system')}
              >
                <FaDesktop />
                System
              </button>
            </div>
          </div>

          {userRole === 'Admin' ? (
            <>
              {/* Currency */}
              <div>
                <div className="st-section-header">
                  <FaMoneyBillWave /> Currency
                </div>
                <select
                  className="st-select"
                  value={currency}
                  onChange={e => handleCurrency(e.target.value)}
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Language */}
              <div>
                <div className="st-section-header">
                  <FaGlobe /> Language
                </div>
                <select
                  className="st-select"
                  value={language}
                  onChange={e => handleLanguage(e.target.value)}
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <div className="st-section-header">
                  <FaFlag /> Country / Phone Code
                </div>
                <select
                  className="st-select"
                  value={country}
                  onChange={e => handleCountry(e.target.value)}
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label} ({c.isd})</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              {/* Currency — read-only */}
              <div>
                <div className="st-section-header">
                  <FaMoneyBillWave /> Currency
                </div>
                <select className="st-select" value={currency} disabled>
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Language — read-only */}
              <div>
                <div className="st-section-header">
                  <FaGlobe /> Language
                </div>
                <select className="st-select" value={language} disabled>
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>

              {/* Country — read-only */}
              <div>
                <div className="st-section-header">
                  <FaFlag /> Country / Phone Code
                </div>
                <select className="st-select" value={country} disabled>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label} ({c.isd})</option>
                  ))}
                </select>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default Settings;
